import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

const History = () => {
  const { user } = useAuth()
  const [userSubscription, setUserSubscription] = useState('free')
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const itemsPerPage = 10

  useEffect(() => {
    const checkSubscription = async () => {
      if (user) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('membership')
          .eq('id', user.id)
          .single();
        
        setUserSubscription(profileData?.membership || 'free');
      }
    };
    
    checkSubscription();
  }, [user]);

  useEffect(() => {
    if (user) {
      getHistory()
    }
  }, [user, currentPage])

  const getHistory = async () => {
    try {
      setLoading(true)
      
      console.log('Fetching history for user:', user.id)

      // Get total count
      const { count, error: countError } = await supabase
        .from('prompt_history')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('deleted', false)

      if (countError) {
        console.error('Error getting count:', countError)
        throw countError
      }

      console.log('Total count:', count)
      
      setTotalPages(Math.ceil(count / itemsPerPage))

      // Get paginated data
      const { data, error } = await supabase
        .from('prompt_history')
        .select('*')
        .eq('user_id', user.id)
        .eq('deleted', false)
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1)

      if (error) {
        console.error('Error getting data:', error)
        throw error
      }

      console.log('Fetched data:', data)
      setHistory(data)
    } catch (error) {
      console.error('Error loading history:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (id, fileUrl) => {
    try {
      // Update downloaded status
      const { error } = await supabase
        .from('prompt_history')
        .update({ downloaded: true })
        .eq('id', id)

      if (error) throw error

      // Download file
      window.open(fileUrl, '_blank')
      
      // Refresh history
      getHistory()
    } catch (error) {
      console.error('Error updating download status:', error.message)
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRemainingDays = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const expires = new Date(created.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7 วัน
    
    // แก้ไขการคำนวณ
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>ประวัติการสร้าง Prompt</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-6xl mx-auto p-6">
          <div className="bg-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
            <h1 className="text-2xl font-bold mb-6">ประวัติการสร้าง Prompt</h1>

            {loading ? (
              <div className="text-center py-8">
                <div className="text-xl">กำลังโหลด...</div>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                ไม่พบประวัติการสร้าง Prompt
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b border-gray-700">
                        <th className="pb-3 px-4">วันที่สร้าง</th>
                        <th className="pb-3 px-4">ชื่อเซ็ท</th>
                        <th className="pb-3 px-4">สันหมดอายุ</th>
                        <th className="pb-3 px-4">ดาวน์โหลด</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((item) => (
                        <tr key={item.id} className="border-b border-gray-800">
                          <td className="py-4 px-4">{formatDate(item.created_at)}</td>
                          <td className="py-4 px-4">{item.set_name}</td>
                          <td className="py-4 px-4">
                            {getRemainingDays(item.created_at) > 0 ? (
                              <span className="text-gray-400">
                                {formatDate(new Date(new Date(item.created_at).getTime() + (7 * 24 * 60 * 60 * 1000)))}
                              </span>
                            ) : (
                              <span className="text-red-400">หมดอายุแล้ว</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <button
                              onClick={() => handleDownload(item.id, item.file_url)}
                              disabled={getRemainingDays(item.created_at) <= 0 || userSubscription === 'free'}
                              className={`px-3 py-1.5 rounded transition-colors ${
                                getRemainingDays(item.created_at) > 0 && userSubscription !== 'free'
                                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                                  : 'bg-gray-600 text-gray-300 cursor-not-allowed'
                              }`}
                            >
                              {userSubscription === 'free' ? 'สำหรับ VIP เท่านั้น' :
                                getRemainingDays(item.created_at) > 0 ? 'ดาวน์โหลด' : 'ไม่สามารถดาวน์โหลดได้'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 bg-gray-800 rounded disabled:opacity-50"
                    >
                      ก่อนหน้า
                    </button>
                    <span className="px-3 py-1">
                      หน้า {currentPage} จาก {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 bg-gray-800 rounded disabled:opacity-50"
                    >
                      ถัดไป
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default History 